import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Plus, RotateCcw, Trash2, Undo2, X } from 'lucide-react';
import { Botao, Cartao, Selo, type CorSelo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Alternador, Entrada, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { nomeDaEmpresa, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, api, del, get, getTodos, mensagemDeErro, patch, post, traduzirErro } from '../../nucleo/api';
import { data, textoLivre } from '../../nucleo/formato';
import type { PropsCartaoNaFicha } from '../../nucleo/modulos-web';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';

/** Espelho de `modules/assinaturas/src/ciclo` (config, modelo e acompanhamento). */
type TipoEtapa = 'dados_fiscais' | 'convite_portal' | 'cobranca_pix' | 'boas_vindas' | 'manual';
type EstadoPasso = 'pendente' | 'aguardando' | 'concluido' | 'falhou' | 'dispensado';

interface ConfigCiclo {
  ganho_ativa_contrato: boolean;
  onboarding_ativo: boolean;
  tarefas: boolean;
  quadro_id: string | null;
  /** Pacote 2: tolerância da inadimplência em dias (0 a 90). */
  inadimplencia_dias: number;
  /** D24 L2: empresas em que o contrato em rascunho ativa quando todos assinam o documento dele. */
  ativar_ao_assinar: string[];
  /** Item 16: o que o ganho dispara para cada contrato (aviso e card no Tarefas). */
  ganho_avisar: string[];
  ganho_tarefa: boolean;
  ganho_quadro_id: string | null;
  ganho_responsavel_id: string | null;
  tarefas_instalado: boolean;
}

interface Etapa {
  id: string | null;
  tipo: TipoEtapa;
  titulo: string;
  descricao: string;
  ordem: number;
  prazo_dias: number | null;
}

interface Passo {
  id: string;
  tipo: TipoEtapa;
  titulo: string;
  descricao: string;
  prazo: string | null;
  estado: EstadoPasso;
  detalhe: string | null;
  referencia: { mensagem?: string; tarefa_numero?: number };
}

interface OnboardingCliente {
  id: string;
  contrato_numero: string;
  /** V3 verticais: os onboardings do mesmo ganho (um por contrato, um contrato por empresa) formam o onboarding único do cliente. */
  grupo_id: string;
  empresa_id: string | null;
  estado: 'em_andamento' | 'concluido' | 'cancelado';
  created_at: string;
  concluido_em: string | null;
  passos: Passo[];
}

const COR_PASSO: Record<EstadoPasso, CorSelo> = { pendente: 'laranja', aguardando: 'azul', concluido: 'verde', falhou: 'perigo', dispensado: 'neutro' };
const COR_ONBOARDING: Record<OnboardingCliente['estado'], CorSelo> = { em_andamento: 'laranja', concluido: 'verde', cancelado: 'neutro' };
const AUTOMATICAS = new Set<TipoEtapa>(['dados_fiscais', 'convite_portal', 'cobranca_pix', 'boas_vindas']);

/** Configuração do ciclo do cliente (contrato ativo no ganho, onboarding na ativação, tarefas das etapas manuais). */
function ConfigCicloCartao() {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/assinaturas/ciclo'], queryFn: () => get<ConfigCiclo>('/assinaturas/ciclo') });
  const quadros = useQuery({
    queryKey: ['assinaturas', 'quadros'],
    queryFn: () => getTodos<{ id: string; nome: string }>('/tarefas/quadros').then((r) => r.itens),
    enabled: !!q.data?.tarefas_instalado && s.pode('tarefas.ver'),
  });
  const membros = useQuery({
    queryKey: ['assinaturas', 'membros'],
    queryFn: () => getTodos<{ id: string; nome: string }>('/membros').then((r) => r.itens),
    enabled: !!q.data,
  });
  const [cfg, setCfg] = useState<Omit<ConfigCiclo, 'tarefas_instalado' | 'inadimplencia_dias'> | null>(null);
  // Pacote 2: a tolerância da inadimplência como texto do campo (vazio ou fora da faixa não salva).
  const [dias, setDias] = useState('');
  useEffect(() => {
    if (q.data) {
      setCfg({
        ganho_ativa_contrato: q.data.ganho_ativa_contrato,
        onboarding_ativo: q.data.onboarding_ativo,
        tarefas: q.data.tarefas,
        quadro_id: q.data.quadro_id,
        ativar_ao_assinar: q.data.ativar_ao_assinar,
        ganho_avisar: q.data.ganho_avisar ?? [],
        ganho_tarefa: !!q.data.ganho_tarefa,
        ganho_quadro_id: q.data.ganho_quadro_id ?? null,
        ganho_responsavel_id: q.data.ganho_responsavel_id ?? null,
      });
      setDias(String(q.data.inadimplencia_dias));
    }
  }, [q.data]);
  const diasValidos = /^\d{1,2}$/.test(dias.trim()) && Number(dias) <= 90;
  const salvar = useMutation({
    // D24 L2: só as empresas do seletor (a que saiu do grupo deixa de constar ao salvar).
    mutationFn: () => api('PUT', '/assinaturas/ciclo', { ...cfg, ativar_ao_assinar: cfg?.ativar_ao_assinar.filter((x) => s.empresas?.opcoes.some((e) => e.id === x)), inadimplencia_dias: Number(dias) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/assinaturas/ciclo'] });
      toast.sucesso(tn('comum.salvo'));
    },
    // Review r55 visual (B3): a recusa por falta de `portal.acessos` diz qual permissão falta, no idioma da pessoa.
    onError: (e) => toast.erro(e instanceof ErroRequisicao && e.metaDoErro()?.permissao === 'portal.acessos' ? t('onboarding.config.exigePortal') : mensagemDeErro(e)),
  });
  // Review r46 visual M1: vale para o workspace inteiro; o restrito não vê o que o servidor recusa (ciclo_exige_todas).
  // Val5 (29/10): ciclo e modelo de onboarding são configuração (`assinaturas.configurar`).
  const podeEditar = podeNoWorkspace(s, 'assinaturas.configurar');
  // Review r55 visual (B3): com o Portal instalado, LIGAR o onboarding ou o contrato ativo no ganho exige também
  // `portal.acessos` (o servidor recusa sem ela); desligar segue livre. O alternador fica desabilitado, com o motivo.
  const semPortal = s.instalado('portal') && !podeNoWorkspace(s, 'portal.acessos');
  // Review r56 visual (B1): a trava é pelo valor GRAVADO (o servidor só recusa ligar o que está desligado lá): quem desliga
  // por engano religa antes de salvar, e o alternador não fica desabilitado com o foco nele.
  const travaOnboarding = semPortal && !q.data?.onboarding_ativo;
  const travaGanho = semPortal && !q.data?.ganho_ativa_contrato;
  const muda = (p: Partial<NonNullable<typeof cfg>>) => setCfg((c) => (c ? { ...c, ...p } : c));
  return (
    <Cartao titulo={t('onboarding.config.titulo')}>
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {q.data && cfg && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-ciclo-config>
          <div className="flex min-w-0 flex-col gap-1">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t('onboarding.config.onboarding')}</span>
              <Alternador marcado={cfg.onboarding_ativo} aoMudar={(v) => muda({ onboarding_ativo: v })} rotulo={t('onboarding.config.onboarding')} desabilitado={!podeEditar || travaOnboarding} />
            </label>
            <p className="text-xs text-tinta-3">{t('onboarding.config.onboardingAjuda')}</p>
            {podeEditar && travaOnboarding && <p className="text-xs text-tinta-3">{t('onboarding.config.exigePortal')}</p>}
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t('onboarding.config.ganhoAtivo')}</span>
              <Alternador marcado={cfg.ganho_ativa_contrato} aoMudar={(v) => muda({ ganho_ativa_contrato: v })} rotulo={t('onboarding.config.ganhoAtivo')} desabilitado={!podeEditar || travaGanho} />
            </label>
            <p className="text-xs text-tinta-3">{t('onboarding.config.ganhoAtivoAjuda')}</p>
            {podeEditar && travaGanho && <p className="text-xs text-tinta-3">{t('onboarding.config.exigePortal')}</p>}
          </div>
          {q.data.tarefas_instalado && (
            <div className="flex min-w-0 flex-col gap-3">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-tinta-2">{t('onboarding.config.tarefas')}</span>
                <Alternador marcado={cfg.tarefas} aoMudar={(v) => muda({ tarefas: v })} rotulo={t('onboarding.config.tarefas')} desabilitado={!podeEditar} />
              </label>
              {cfg.tarefas && quadros.data && (
                <Rotulo id="ciclo-quadro" rotulo={t('onboarding.config.quadro')}>
                  <Selecao id="ciclo-quadro" disabled={!podeEditar} vazio={t('onboarding.config.quadroPadrao')} value={cfg.quadro_id ?? ''} onChange={(e) => muda({ quadro_id: e.target.value || null })} opcoes={quadros.data.map((x) => ({ valor: x.id, rotulo: x.nome }))} />
                </Rotulo>
              )}
            </div>
          )}
          {s.instalado('documentos') && !!s.empresas?.opcoes.length && (
            <div className="flex min-w-0 flex-col gap-2" data-ativar-ao-assinar>
              <span className="text-sm font-medium text-tinta-2">{t('onboarding.config.ativarAoAssinar')}</span>
              {s.empresas.opcoes.map((e) => (
                <label key={e.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-tinta-2" title={e.nome}>
                    {e.nome}
                  </span>
                  <Alternador
                    marcado={cfg.ativar_ao_assinar.includes(e.id)}
                    aoMudar={(v) => muda({ ativar_ao_assinar: v ? [...cfg.ativar_ao_assinar, e.id] : cfg.ativar_ao_assinar.filter((x) => x !== e.id) })}
                    rotulo={`${t('onboarding.config.ativarAoAssinar')}: ${e.nome}`}
                    desabilitado={!podeEditar}
                  />
                </label>
              ))}
              <p className="text-xs text-tinta-3">{t('onboarding.config.ativarAoAssinarAjuda')}</p>
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <Rotulo id="ciclo-inadimplencia" rotulo={t('onboarding.config.inadimplencia')} ajuda={t('onboarding.config.inadimplenciaAjuda')} erro={diasValidos ? undefined : t('onboarding.config.inadimplenciaFaixa')}>
              <Entrada
                id="ciclo-inadimplencia"
                type="number"
                inputMode="numeric"
                min={0}
                max={90}
                disabled={!podeEditar}
                value={dias}
                aria-invalid={diasValidos ? undefined : true}
                aria-describedby={diasValidos ? 'ciclo-inadimplencia-ajuda' : 'ciclo-inadimplencia-erro'}
                onChange={(e) => setDias(e.target.value)}
                className="numero"
              />
            </Rotulo>
          </div>
          <div className="flex min-w-0 flex-col gap-3 md:col-span-3" data-ciclo-ganho>
            <span className="text-sm font-semibold text-tinta-1">{t('onboarding.config.ganho')}</span>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-2">
                <span className="text-sm font-medium text-tinta-2">{t('onboarding.config.ganhoAvisar')}</span>
                {!cfg.ganho_avisar.length && <p className="text-sm text-tinta-3">{t('onboarding.config.ganhoAvisarTodos')}</p>}
                {!!cfg.ganho_avisar.length && (
                  <ul className="flex flex-wrap gap-2">
                    {cfg.ganho_avisar.map((u) => {
                      const nome = membros.data?.find((m) => m.id === u)?.nome ?? '…';
                      return (
                        <li key={u} className="flex items-center gap-1">
                          <Selo>{nome}</Selo>
                          {podeEditar && <Botao variante="fantasma" aria-label={t('onboarding.config.ganhoAvisarRemover', { nome })} title={t('onboarding.config.ganhoAvisarRemover', { nome })} icone={<X className="size-4" />} onClick={() => muda({ ganho_avisar: cfg.ganho_avisar.filter((x) => x !== u) })} />}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {podeEditar && membros.data && (
                  <Rotulo id="ciclo-ganho-avisar" rotulo={t('onboarding.config.ganhoAvisarIncluir')}>
                    <Selecao
                      id="ciclo-ganho-avisar"
                      vazio={t('onboarding.config.ganhoAvisarIncluir')}
                      value=""
                      onChange={(e) => e.target.value && muda({ ganho_avisar: [...cfg.ganho_avisar, e.target.value] })}
                      opcoes={membros.data.filter((m) => !cfg.ganho_avisar.includes(m.id)).map((m) => ({ valor: m.id, rotulo: m.nome }))}
                    />
                  </Rotulo>
                )}
                <p className="text-xs text-tinta-3">{t('onboarding.config.ganhoAvisarAjuda')}</p>
              </div>
              {q.data.tarefas_instalado && (
                <div className="flex min-w-0 flex-col gap-3">
                  <label className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-tinta-2">{t('onboarding.config.ganhoTarefa')}</span>
                    <Alternador marcado={cfg.ganho_tarefa} aoMudar={(v) => muda({ ganho_tarefa: v })} rotulo={t('onboarding.config.ganhoTarefa')} desabilitado={!podeEditar} />
                  </label>
                  {cfg.ganho_tarefa && quadros.data && (
                    <Rotulo id="ciclo-ganho-quadro" rotulo={t('onboarding.config.ganhoQuadro')}>
                      <Selecao id="ciclo-ganho-quadro" disabled={!podeEditar} vazio={t('onboarding.config.quadroPadrao')} value={cfg.ganho_quadro_id ?? ''} onChange={(e) => muda({ ganho_quadro_id: e.target.value || null })} opcoes={quadros.data.map((x) => ({ valor: x.id, rotulo: x.nome }))} />
                    </Rotulo>
                  )}
                  {cfg.ganho_tarefa && membros.data && (
                    <Rotulo id="ciclo-ganho-responsavel" rotulo={t('onboarding.config.ganhoResponsavel')}>
                      <Selecao id="ciclo-ganho-responsavel" disabled={!podeEditar} vazio={t('onboarding.config.semResponsavel')} value={cfg.ganho_responsavel_id ?? ''} onChange={(e) => muda({ ganho_responsavel_id: e.target.value || null })} opcoes={membros.data.map((m) => ({ valor: m.id, rotulo: m.nome }))} />
                    </Rotulo>
                  )}
                  <p className="text-xs text-tinta-3">{t('onboarding.config.ganhoTarefaAjuda')}</p>
                </div>
              )}
            </div>
          </div>
          {podeEditar && (
            <div className="md:col-span-3">
              <Botao variante="secundario" disabled={!diasValidos} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {tn('comum.salvar')}
              </Botao>
            </div>
          )}
        </div>
      )}
    </Cartao>
  );
}

/** Formulário da etapa (nova manual ou edição): título, descrição (na boas-vindas, o texto da mensagem) e prazo. */
function FormEtapa({ etapa, aoFechar }: { etapa: Etapa | 'nova'; aoFechar: () => void }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const nova = etapa === 'nova';
  const campos: DefCampo[] = [
    { nome: 'titulo', rotulo: t('onboarding.modelo.campoTitulo'), tipo: 'texto', obrigatorio: true },
    { nome: 'descricao', rotulo: !nova && etapa.tipo === 'boas_vindas' ? t('onboarding.modelo.campoMensagem') : t('onboarding.modelo.campoDescricao'), tipo: 'area' },
    { nome: 'prazo_dias', rotulo: t('onboarding.modelo.campoPrazo'), tipo: 'numero', min: 0, max: 365, passo: 1, largura: 'meia' },
  ];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={nova ? t('onboarding.modelo.nova') : t('onboarding.modelo.editar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-etapa-onboarding">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-etapa-onboarding"
        campos={campos}
        inicial={nova ? { titulo: '', descricao: '', prazo_dias: 7 } : { titulo: etapa.titulo, descricao: etapa.descricao, prazo_dias: etapa.prazo_dias }}
        aoEnviar={async (v) => {
          const corpo = { titulo: String(v.titulo ?? ''), descricao: String(v.descricao ?? ''), prazo_dias: v.prazo_dias === null || v.prazo_dias === undefined || v.prazo_dias === '' ? null : Number(v.prazo_dias) };
          try {
            if (nova) await post('/assinaturas/onboarding/modelo/etapas', { id: crypto.randomUUID(), tipo: 'manual', ...corpo });
            else await patch(`/assinaturas/onboarding/modelo/etapas/${etapa.id}`, corpo);
            await qc.invalidateQueries({ queryKey: ['/assinaturas/onboarding/modelo'] });
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          } catch (e) {
            toast.erro(mensagemDeErro(e));
          }
        }}
      />
    </Modal>
  );
}

/** Modelo de onboarding do workspace: o padrão até ser personalizado; depois, etapa por etapa. */
function ModeloCartao() {
  const { t } = useTranslation('assinaturas');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['/assinaturas/onboarding/modelo'], queryFn: () => get<{ padrao: boolean; etapas: Etapa[] }>('/assinaturas/onboarding/modelo') });
  const [editando, setEditando] = useState<Etapa | 'nova' | null>(null);
  const [excluindo, setExcluindo] = useState<Etapa | null>(null);
  const invalidar = () => qc.invalidateQueries({ queryKey: ['/assinaturas/onboarding/modelo'] });
  const personalizar = useMutation({ mutationFn: () => post('/assinaturas/onboarding/modelo/personalizar'), onSuccess: invalidar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/assinaturas/onboarding/modelo/etapas/${id}`),
    onSuccess: async () => {
      setExcluindo(null);
      await invalidar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Review r46 visual M1: o modelo vale para todas as empresas (ciclo_exige_todas no servidor).
  // Val5 (29/10): ciclo e modelo de onboarding são configuração (`assinaturas.configurar`).
  const podeEditar = podeNoWorkspace(s, 'assinaturas.configurar');
  return (
    <Cartao
      titulo={t('onboarding.modelo.titulo')}
      acao={
        podeEditar &&
        q.data &&
        (q.data.padrao ? (
          <Botao tamanho="sm" carregando={personalizar.isPending} icone={<Pencil className="size-4" aria-hidden />} onClick={() => personalizar.mutate()}>
            {t('onboarding.modelo.personalizar')}
          </Botao>
        ) : (
          <Botao tamanho="sm" icone={<Plus className="size-4" aria-hidden />} onClick={() => setEditando('nova')}>
            {t('onboarding.modelo.nova')}
          </Botao>
        ))
      }
    >
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {q.data && (
        <>
          <p className="mb-3 text-sm text-tinta-3">{q.data.padrao ? t('onboarding.modelo.padraoAjuda') : t('onboarding.modelo.ajuda')}</p>
          <ol className="divide-y divide-borda" data-modelo-onboarding>
            {q.data.etapas.map((e, i) => (
              <li key={e.id ?? `${e.tipo}-${i}`} className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-1 py-2.5">
                <span className="numero w-6 shrink-0 text-sm text-tinta-3">{i + 1}.</span>
                <div className="flex min-w-0 flex-1 basis-56 flex-col gap-1">
                  <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium">
                    {textoLivre(e.titulo)}
                    <Selo cor={AUTOMATICAS.has(e.tipo) ? 'azul' : 'neutro'}>{t(`onboarding.tipos.${e.tipo}`)}</Selo>
                  </span>
                  {e.descricao && <TextoCortado className="text-xs text-tinta-3">{textoLivre(e.descricao)}</TextoCortado>}
                </div>
                <span className="text-xs text-tinta-3">{e.prazo_dias === null ? t('onboarding.modelo.semPrazo') : t('onboarding.modelo.prazo', { count: e.prazo_dias })}</span>
                {podeEditar && e.id && (
                  <span className="flex gap-1">
                    <Botao variante="fantasma" tamanho="sm" aria-label={`${t('onboarding.modelo.editar')}: ${textoLivre(e.titulo)}`} title={t('onboarding.modelo.editar')} icone={<Pencil className="size-4" />} onClick={() => setEditando(e)} />
                    <Botao variante="fantasma" tamanho="sm" aria-label={`${t('onboarding.modelo.remover')}: ${textoLivre(e.titulo)}`} title={t('onboarding.modelo.remover')} icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(e)} />
                  </span>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
      {editando && <FormEtapa etapa={editando} aoFechar={() => setEditando(null)} />}
      <Confirmar
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => excluindo?.id && excluir.mutate(excluindo.id)}
        carregando={excluir.isPending}
        perigoso
        titulo={t('onboarding.modelo.removerTitulo')}
        texto={t('onboarding.modelo.removerTexto')}
      />
    </Cartao>
  );
}

/** Aba Onboarding de Comercial > Assinaturas: a configuração do ciclo do cliente e o modelo das etapas. */
export function PaginaOnboarding() {
  return (
    <div className="flex flex-col gap-4">
      <ConfigCicloCartao />
      <ModeloCartao />
    </div>
  );
}

/**
 * Texto do motivo da etapa: o código conhecido no idioma; o resto (o código do módulo que recusou, como
 * `portal.termo_ausente`) pelo dicionário de erros, com a mensagem gravada só quando o código não tem tradução (r54 visual B5).
 */
function useDetalhe() {
  const { t, i18n } = useTranslation('assinaturas');
  return (p: Passo) => {
    if (!p.detalhe) return null;
    // Review r55 visual (B2): na manual, o que faltou foi o card da tarefa (não a etapa): texto próprio.
    // Review rl30-5 visual (B2): na etapa manual, o que faltou foi a tarefa, também quando a ação foi feita numa simulação.
    const manual = p.tipo === 'manual' && (p.detalhe === 'sem_permissao' || p.detalhe === 'simulacao');
    const chave = manual ? `onboarding.detalhes.${p.detalhe}_manual` : `onboarding.detalhes.${p.detalhe.replace(/\./g, '_')}`;
    if (i18n.exists(chave, { ns: 'assinaturas' })) return t(chave, { numero: p.referencia.tarefa_numero ?? '' });
    return traduzirErro(p.detalhe, p.referencia.mensagem ?? p.detalhe);
  };
}

/** V3 verticais: os onboardings agrupados pelo ganho (o grupo na ordem do mais recente), cada grupo na ordem da abertura. */
function porGrupo(itens: OnboardingCliente[]): OnboardingCliente[][] {
  const grupos = new Map<string, OnboardingCliente[]>();
  for (const o of itens) grupos.set(o.grupo_id, [...(grupos.get(o.grupo_id) ?? []), o]);
  return [...grupos.values()].map((g) => [...g].sort((a, b) => a.created_at.localeCompare(b.created_at)));
}

/**
 * Trilha 4: cartão "Onboarding" na ficha do cliente (Cadastros), com o Assinaturas instalado e `assinaturas.ver`. Cada
 * onboarding (um por contrato ativado) com as etapas, o estado e o motivo; quem edita marca, dispensa, reabre e executa de
 * novo as automáticas pendentes ou com falha. V3 verticais: os contratos do mesmo ganho (um por empresa) aparecem juntos,
 * como o onboarding único do cliente; as etapas do cliente ficam no primeiro, as de cada contrato no dele.
 */
export function CartaoOnboarding({ parceiroId, nome }: PropsCartaoNaFicha) {
  const { t } = useTranslation('assinaturas');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const detalhe = useDetalhe();
  const empresas = useEmpresasDoCatalogo();
  const pode = s.pode('assinaturas.ver');
  const chave = ['/assinaturas/onboarding', parceiroId];
  const q = useQuery({ queryKey: chave, queryFn: () => get<{ itens: OnboardingCliente[] }>(`/assinaturas/parceiros/${parceiroId}/onboarding`), enabled: pode });
  const recarregar = () => qc.invalidateQueries({ queryKey: chave });
  const executar = useMutation({
    mutationFn: (id: string) => post(`/assinaturas/onboarding/${id}/executar`),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const marcar = useMutation({
    mutationFn: (a: { id: string; passo: string; acao: 'concluir' | 'dispensar' | 'reabrir' }) => post(`/assinaturas/onboarding/${a.id}/passos/${a.passo}`, { acao: a.acao }),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!pode || !q.data?.itens.length) return null;
  const podeEditar = s.pode('assinaturas.editar');
  const nomeEmpresa = (id: string | null) => {
    const e = id && (empresas.data?.empresas.length ?? 0) > 1 ? empresas.data?.empresas.find((x) => x.id === id) : null;
    return e ? nomeDaEmpresa(e) : null;
  };
  return (
    <Cartao titulo={t('onboarding.ficha.titulo')}>
      <div className="flex flex-col gap-5" data-onboarding-cliente>
        {porGrupo(q.data.itens).map((grupo) => (
          <div key={grupo[0]!.grupo_id} className={grupo.length > 1 ? 'flex flex-col gap-4 rounded-lg border border-borda p-3' : 'contents'} data-onboarding-grupo={grupo[0]!.grupo_id}>
            {grupo.length > 1 && <span className="text-xs font-medium text-tinta-2">{t('onboarding.ficha.unico', { count: grupo.length })}</span>}
        {grupo.map((o) => {
          // Review r55 visual (B2): também a etapa (inclusive a manual) que ficou sem a permissão de quem ativou: o motivo manda
          // usar "Executar de novo", e o botão roda de novo todas as etapas não concluídas com a permissão de quem clica.
          const pendentesAuto =
            o.estado === 'em_andamento' &&
            o.passos.some((p) => (AUTOMATICAS.has(p.tipo) && (p.estado === 'pendente' || p.estado === 'falhou' || p.estado === 'aguardando')) || ((p.detalhe === 'sem_permissao' || p.detalhe === 'simulacao') && p.estado !== 'concluido' && p.estado !== 'dispensado'));
          return (
            <section key={o.id} className="flex min-w-0 flex-col gap-2" aria-label={t('onboarding.ficha.doContrato', { numero: o.contrato_numero })}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="numero text-sm font-medium">{o.contrato_numero}</span>
                {nomeEmpresa(o.empresa_id) && <Selo cor="ciano">{nomeEmpresa(o.empresa_id)}</Selo>}
                <Selo cor={COR_ONBOARDING[o.estado]}>{t(`onboarding.estadoOnboarding.${o.estado}`)}</Selo>
                <span className="text-xs text-tinta-3">{t('onboarding.ficha.desde', { data: data(o.created_at) })}</span>
                {podeEditar && pendentesAuto && (
                  <Botao tamanho="sm" className="ml-auto" carregando={executar.isPending && executar.variables === o.id} icone={<RotateCcw className="size-4" aria-hidden />} onClick={() => executar.mutate(o.id)}>
                    {t('onboarding.ficha.executar')}
                  </Botao>
                )}
              </div>
              <ol className="divide-y divide-borda rounded-lg border border-borda">
                {o.passos.map((p) => {
                  const motivo = detalhe(p);
                  const fechado = p.estado === 'concluido' || p.estado === 'dispensado';
                  return (
                    <li key={p.id} className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2" data-passo={p.tipo} data-estado={p.estado}>
                      <div className="flex min-w-0 flex-1 basis-56 flex-col gap-0.5">
                        <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium">
                          {textoLivre(p.titulo, nome)}
                          <Selo cor={COR_PASSO[p.estado]}>{t(`onboarding.estadoPasso.${p.estado}`)}</Selo>
                        </span>
                        {motivo && <span className="text-xs text-tinta-3">{textoLivre(motivo, nome)}</span>}
                      </div>
                      {p.prazo && !fechado && <span className="text-xs text-tinta-3">{t('onboarding.ficha.prazo', { data: data(p.prazo) })}</span>}
                      {podeEditar && o.estado !== 'cancelado' && (
                        <span className="flex gap-1">
                          {fechado ? (
                            <Botao variante="fantasma" tamanho="sm" aria-label={`${t('onboarding.ficha.reabrir')}: ${textoLivre(p.titulo, nome)}`} title={t('onboarding.ficha.reabrir')} icone={<Undo2 className="size-4" />} onClick={() => marcar.mutate({ id: o.id, passo: p.id, acao: 'reabrir' })} />
                          ) : (
                            <>
                              <Botao variante="fantasma" tamanho="sm" aria-label={`${t('onboarding.ficha.concluir')}: ${textoLivre(p.titulo, nome)}`} title={t('onboarding.ficha.concluir')} icone={<Check className="size-4" />} onClick={() => marcar.mutate({ id: o.id, passo: p.id, acao: 'concluir' })} />
                              <Botao variante="fantasma" tamanho="sm" aria-label={`${t('onboarding.ficha.dispensar')}: ${textoLivre(p.titulo, nome)}`} title={t('onboarding.ficha.dispensar')} icone={<X className="size-4" />} onClick={() => marcar.mutate({ id: o.id, passo: p.id, acao: 'dispensar' })} />
                            </>
                          )}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}
          </div>
        ))}
      </div>
    </Cartao>
  );
}
